import { BidStatus, OrganizationWaybillMatchingSettingEditModel } from "api";
import { numberInRange } from "helpers/validators";

export type NumericValue = number | "";

export type MatchingSettingsForm = {
    disableWaybillMatching: boolean;
    useShippingOrderNumber: boolean;
    bid: {
        useCreateDate: boolean;
        createDateMatchingPeriodInHours: NumericValue;
        allowedStatuses: BidStatus[];
        useCounterparty: boolean;
    };
    loadPoint: {
        usePlanEnterDate: boolean;
        planEnterDateMatchingPeriodInHours: NumericValue;
        useLocation: boolean;
        matchingRadiusInKilometers: NumericValue;
    };
    unloadPoint: {
        useCounterparty: boolean;
        useLocation: boolean;
        matchingRadiusInKilometers: NumericValue;
    };
};

export type UpdateForm = (updater: (current: MatchingSettingsForm) => MatchingSettingsForm) => void;

export const MIN_MATCHING_PERIOD_IN_HOURS = 1;
export const MAX_MATCHING_PERIOD_IN_HOURS = 240;
export const MIN_MATCHING_RADIUS_IN_METERS = 1000;
export const MAX_MATCHING_RADIUS_IN_METERS = 10000;

export const selectableStatuses = [
    { value: BidStatus.Planned, label: "Запланирована" },
    { value: BidStatus.Started, label: "Активная" },
    { value: BidStatus.Done, label: "Выполнена" },
];

export const toFormModel = (waybillSettings: OrganizationWaybillMatchingSettingEditModel): MatchingSettingsForm => ({
    disableWaybillMatching: waybillSettings.disableMatching,
    useShippingOrderNumber: waybillSettings.useShippingOrderNumber,
    bid: {
        useCreateDate: waybillSettings.bid.useCreateDate,
        createDateMatchingPeriodInHours: waybillSettings.bid.createDateMatchingPeriodInHours,
        allowedStatuses: waybillSettings.bid.allowedStatuses,
        useCounterparty: waybillSettings.bid.useCounterparty ?? false,
    },
    loadPoint: {
        usePlanEnterDate: waybillSettings.loadPoint.usePlanEnterDate,
        planEnterDateMatchingPeriodInHours: waybillSettings.loadPoint.planEnterDateMatchingPeriodInHours,
        useLocation: waybillSettings.loadPoint.useLocation,
        matchingRadiusInKilometers: waybillSettings.loadPoint.matchingRadius / 1000,
    },
    unloadPoint: {
        useCounterparty: waybillSettings.unloadPoint.useCounterparty,
        useLocation: waybillSettings.unloadPoint.useLocation,
        matchingRadiusInKilometers: waybillSettings.unloadPoint.matchingRadius / 1000,
    },
});

export const matchingPeriodValidator = (value = "") => {
    const numericValue = Number(value);
    if (!Number.isInteger(numericValue)) {
        return "Введите целое количество часов";
    }
    return numberInRange(value, MIN_MATCHING_PERIOD_IN_HOURS, MAX_MATCHING_PERIOD_IN_HOURS);
};

export const matchingRadiusValidator = (value = "") =>
    numberInRange(value, MIN_MATCHING_RADIUS_IN_METERS / 1000, MAX_MATCHING_RADIUS_IN_METERS / 1000);

export const toNumericValue = (value: unknown): NumericValue => (typeof value === "number" ? value : "");

export const toRequestModel = (form: MatchingSettingsForm): OrganizationWaybillMatchingSettingEditModel => ({
    disableMatching: form.disableWaybillMatching,
    useShippingOrderNumber: form.useShippingOrderNumber,
    bid: {
        useCreateDate: form.bid.useCreateDate,
        createDateMatchingPeriodInHours: Number(form.bid.createDateMatchingPeriodInHours),
        allowedStatuses: form.bid.allowedStatuses,
        useCounterparty: form.bid.useCounterparty,
    },
    loadPoint: {
        usePlanEnterDate: form.loadPoint.usePlanEnterDate,
        planEnterDateMatchingPeriodInHours: Number(form.loadPoint.planEnterDateMatchingPeriodInHours),
        useLocation: form.loadPoint.useLocation,
        matchingRadius: Number(form.loadPoint.matchingRadiusInKilometers) * 1000,
    },
    unloadPoint: {
        useCounterparty: form.unloadPoint.useCounterparty,
        useLocation: form.unloadPoint.useLocation,
        matchingRadius: Number(form.unloadPoint.matchingRadiusInKilometers) * 1000,
    },
});

export const isFormValid = (form: MatchingSettingsForm) =>
    !matchingPeriodValidator(String(form.bid.createDateMatchingPeriodInHours)) &&
    !matchingPeriodValidator(String(form.loadPoint.planEnterDateMatchingPeriodInHours)) &&
    !matchingRadiusValidator(String(form.loadPoint.matchingRadiusInKilometers)) &&
    !matchingRadiusValidator(String(form.unloadPoint.matchingRadiusInKilometers));
